<template>
  <standard-layout title="FoosMonster">
    <div class="homepage-banner">
      <img
        src="/img/homepage.jpeg"
        class="homepage-image"
        alt=""
      />
    </div>

    <section class="section-hero">
      <v-container fluid style="padding-top: 96px; padding-bottom: 96px;">
        <v-row justify="center" align="center">
          <v-col cols="12" md="4" class="text-center">
            <p class="text-h6 mb-2"><strong>FoosMonster</strong> keeps your tables packed and your matches fair.</p>

            <p class="text-body-1">
              Built for the <strong>Austin Foosball Club</strong>,
              our app matches players based on live skill ratings&mdash;so beginners don't get
              crushed, pros get real competition, and no table ever sits empty.
            </p>

            <p class="text-body-1 font-weight-bold mb-4">
              More games. Better matchups. Maximum fun.
            </p>

            <v-btn
              color="primary"
              size="large"
              style="margin-top: 90px;"
              @click="$router.push({ name: 'tournamentNew' })"
            >
              Create Tournament
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="section-why pt-4 pb-16">
      <v-container style="max-width: 750px;">
        <h2 class="text-h4 text-center mb-4">Why?</h2>
        <p class="text-body-1 text-center mb-8">
          Existing tournament software has served us happily for many years and, 90% of the time,
          things ran smoothly. But depending on the player turnout, we could have a lot of players waiting&mdash;even if a table was available.
        </p>
        <v-row class="wrap justify-center">
          <v-col
            v-for="item in whyItems"
            :key="item.title"
            cols="12"
            md="6"
          >
            <v-card
              hover
              class="h-100 pa-4"
              elevation="4"
            >
              <v-card-text class="text-center">
                <v-icon
                  :icon="item.icon"
                  size="48"
                  color="error"
                />
              </v-card-text>
              <v-card-title class="d-flex justify-center text-h5 font-weight-bold pa-0 mb-2">
                {{ item.title }}
              </v-card-title>
              <v-card-text class="text-body-2 text-center">
                {{ item.description }}
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="section-how pt-4 pb-16 text-justify">
      <v-container style="max-width: 750px;">
        <h2 class="text-h4 text-center mb-4">How it works.</h2>
        <p class="text-body-1 text-center mb-8">
          So we decided to fix these issues in a way that worked for us.
        </p>
        <v-row class="wrap justify-center">
          <v-col
            v-for="item in howItems"
            :key="item.title"
            cols="12"
            md="6"
          >
            <v-card
              hover
              class="h-100 pa-4"
              elevation="4"
            >
              <v-card-text class="text-center">
                <v-icon
                  :icon="item.icon"
                  size="48"
                  color="success"
                />
              </v-card-text>
              <v-card-title class="d-flex justify-center text-h5 font-weight-bold pa-0 mb-2">
                {{ item.title }}
              </v-card-title>
              <v-card-text class="text-body-2 text-center">
                {{ item.description }}
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="section-footer py-10">
      <v-container class="text-center text-body-2" style="max-width: 750px;">
        <v-divider class="mb-4" />
        Built for the <strong>Austin Foosball Club</strong> &mdash; more games, better matchups, maximum fun.
      </v-container>
    </section>
  </standard-layout>
</template>

<script>
import StandardLayout from "@/components/standard-layout.vue";

export default {
  components: {
    StandardLayout
  },
  data() {
    return {
      whyItems: [
        {
          icon: "mdi-clock-outline",
          title: "Table Congestion",
          description:
            "With 12-15 players and 2 tables, existing software queued 3 matches per round — after 2 finished, 8-11 players sat idle waiting for the 3rd match to end."
        },
        {
          icon: "mdi-account-clock-outline",
          title: "Unnecessary Byes",
          description:
            "With 6-7 players, 2-3 people have a bye when they could've played a meaningful 1v1 or 1v1v1 Ironman match."
        },
        {
          icon: "mdi-sword-cross",
          title: "Rough Matchups",
          description:
            "Pure randomness paired beginners against advanced players — sometimes this was bad for morale, discouraging newcomers."
        },
        {
          icon: "mdi-table-lock",
          title: "No Flexibility",
          description:
            "When a long-running match delayed the round, there was no way to call a new match on the empty table. We were stuck waiting."
        }
      ],
      howItems: [
        {
          icon: "mdi-table-check",
          title: "Full Table Utilization",
          description:
            "Matches are not queued and are only scheduled when a table is available — which means you are never waiting for a single match to complete the round before utilizing empty tables."
        },
        {
          icon: "mdi-fire",
          title: "Ironman Matches",
          description:
            "6-7 players? FoosMonster calls 1v1 and 1v1v1 Ironman matches instead of having players sit. Winners of these matches earn points toward the tournament."
        },
        {
          icon: "mdi-account-group-outline",
          title: "Smart Matchups",
          description:
            "Its not a true Monster DYP actually. The algorithm matches players by OpenSkill rankings (similar to ELO) earned across tournaments, pairing like versus like while letting winners climb quickly during the course of the tournament. In doubles matches, a lower-ranked player is teamed with the higher-ranked player — great for fair competition and mentorship."
        },
        {
          icon: "mdi-toggle-switch-off-outline",
          title: "Singles Opt-out",
          description:
            "Players who dislike singles can simply opt out — they will never be called for a singles match."
        },
        {
          icon: "mdi-gavel",
          title: "Director Discretion",
          description:
            "Two matches start together but one runs long? The tournament director can call a match on the available table, even before the round ends."
        },
        {
          icon: "mdi-account-cancel-outline",
          title: "Absent Player Handling",
          description:
            "Players step out or disappear? Cancel their match, mark them absent, and regenerate with present players who are not penalized for the cancellation. When the player returns, FoosMonster will schedule them until they are caught up."
        }
      ]
    };
  }
};
</script>

<style scoped>

.homepage-banner {
  background-color: #E8F4F8;
  text-align: center;
}

.homepage-image {
  max-height: 650px;
  max-width: 100%;
  width: auto;
  height: auto;
  display: inline-block;
  vertical-align: bottom;
}

section.section-why {
  background-color: var(--background-color);
}

section.section-how {
  background-color: rgba(248, 156, 35, 0.05);
}

section.section-footer {
  background-color: var(--secondary-color);
  color: white;
}

section.section-footer .v-divider {
  border-color: rgba(255, 255, 255, 0.3);
}

</style>
